import { PluginManager } from '../plugins/plugins';
import { Camera } from './camera/camera';
import { Container } from './core/container';
import { Singletons } from './core/interfaces';
import { Scene } from './core/scene';
import { TimeManager } from './core/time-manager';
import { EventBus } from './events/event-bus';
import { EventBusEvent } from './events/event-bus-events';
import { InputManager } from './input/input-manager';
import { WebGLRenderer } from './rendering/webgl-renderer';
import { errorManagerInstance } from './utils/errorManager';
import { isThisNode } from './utils/isThisNode';
export class Engine {
    isRunning = false;
    isReady = false;
    isUpdateTimeThrottle_;
    lastFrameTime_ = 0;
    // Core engine systems
    renderer;
    scene;
    camera;
    inputManager;
    eventBus;
    pluginManager;
    timeManager;
    constructor() {
        // Initialize core engine systems
        this.eventBus = EventBus.getInstance();
        this.renderer = new WebGLRenderer();
        this.scene = Scene.getInstance();
        this.camera = new Camera();
        this.timeManager = new TimeManager();
        this.inputManager = new InputManager();
        this.pluginManager = new PluginManager();
        Container.getInstance().registerSingleton(Singletons.TimeManager, this.timeManager);
        Container.getInstance().registerSingleton(Singletons.WebGLRenderer, this.renderer);
        Container.getInstance().registerSingleton(Singletons.MeshManager, this.renderer.meshManager);
        Container.getInstance().registerSingleton(Singletons.Scene, this.scene);
        Container.getInstance().registerSingleton(Singletons.InputManager, this.inputManager);
        Container.getInstance().registerSingleton(Singletons.MainCamera, this.camera);
    }
    init() {
        this.addErrorTrap_();
        this.eventBus.init();
        this.camera.init(settingsManager);
        this.timeManager.init();
        this.isReady = true;
    }
    addErrorTrap_() {
        window.addEventListener('error', (e) => {
            if (!settingsManager.isGlobalErrorTrapOn) {
                return;
            }
            if (isThisNode()) {
                throw e.error;
            }
            errorManagerInstance.error(e.error, 'Global Error Trapper', e.message);
        });
    }
    run() {
        if (!this.isReady) {
            throw new Error('KeepTrack is not ready');
        }
        if (this.isRunning) {
            throw new Error('KeepTrack is already running');
        }
        this.gameLoop_();
        // Main game loop
        this.isRunning = true;
    }
    gameLoop_(timestamp = 0) {
        requestAnimationFrame(this.gameLoop_.bind(this));
        const dt = (timestamp - this.lastFrameTime_);
        this.lastFrameTime_ = timestamp;
        if (settingsManager.cruncherReady) {
            this.update_(dt); // Do any per frame calculations
            this.draw_(dt);
        }
    }
    update_(dt = 0) {
        this.renderer.dt = dt;
        this.renderer.dtAdjusted = (Math.min(this.renderer.dt / 1000.0, 1.0 / Math.max(this.timeManager.propRate, 0.001)) * this.timeManager.propRate);
        this.timeManager.update();
        // Update official time for everyone else
        this.timeManager.setNow(Date.now());
        if (!this.isUpdateTimeThrottle_) {
            this.isUpdateTimeThrottle_ = true;
            this.timeManager.setSelectedDate(this.timeManager.simulationTimeObj);
            setTimeout(() => {
                this.isUpdateTimeThrottle_ = false;
            }, 500);
        }
        this.eventBus.emit(EventBusEvent.update, dt);
        this.renderer.update();
    }
    draw_(dt = 0) {
        this.camera.draw(this.renderer.sensorPos);
        this.renderer.render(this.scene, this.camera);
        if (Engine.isFpsAboveLimit(dt, 5) && !settingsManager.lowPerf && !settingsManager.isDragging && !settingsManager.isDemoModeOn) {
            this.eventBus.emit(EventBusEvent.highPerformanceRender, dt);
        }
        this.eventBus.emit(EventBusEvent.endOfDraw, dt);
    }
    stop() {
        this.isRunning = false;
    }
    /** Check if the FPS is above a certain threshold */
    static isFpsAboveLimit(dt, minimumFps) {
        return this.getFps_(dt) > minimumFps;
    }
    static getFps_(dt) {
        return 1000 / dt;
    }
}
